import React, { useEffect, useMemo } from "react";

import clsx from "clsx";

import { useCustomMetricsContext } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { type ColumnConfig } from "@features/sales-v3/hooks/use-table-state";
import {
  type MetricCategorizer,
  MetricCategory,
  type SalesPageMetricWithChildren,
} from "@features/sales-v3/utils/metric-categorizer";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { IconButton } from "@shared/buttons";
import { TextInput } from "@shared/text-input";

import { PanelContainer } from "../panel-container";

import { AllMetricsPanel } from "./all-metrics-panel";
import { MetricSelectionGroup } from "./metric-selection-group";

type Props = {
  selectedCategory: MetricCategory;
  metricCategories: MetricCategorizer;
  selectedColumnConfigs: ColumnConfig[];
  selectManyMetrics: (metrics: SalesPageMetricWithChildren[]) => void;
  selectMetric: (metric: SalesPageMetricWithChildren) => void;
  clearAllByCategory: (category: MetricCategory) => void;
  deselectMetric: (metric: SalesPageMetricWithChildren) => void;
  closeModal: () => void;
};

export const MetricsPanel = ({
  selectedCategory,
  metricCategories,
  selectedColumnConfigs,
  selectManyMetrics,
  selectMetric,
  clearAllByCategory,
  deselectMetric,
  closeModal,
}: Props) => {
  const { openModal } = useCustomMetricsContext();
  const metricsByCategory = useMemo(
    () => metricCategories.getMetricsByCategory(selectedCategory),
    [metricCategories, selectedCategory],
  );

  const [filteredMetrics, { search, setSearch, clearSearch }] = useFuzzySearch(
    metricsByCategory,
    {
      searchKeys: ["name"],
    },
  );

  useEffect(() => {
    clearSearch();
  }, [metricsByCategory, clearSearch]);

  return (
    <PanelContainer title="METRICS">
      {selectedCategory === MetricCategory.ALL ? (
        <AllMetricsPanel
          selectedCategory={selectedCategory}
          metricCategories={metricCategories}
          selectedColumnConfigs={selectedColumnConfigs}
          selectManyMetrics={selectManyMetrics}
          selectMetric={selectMetric}
          clearAllByCategory={clearAllByCategory}
          deselectMetric={deselectMetric}
        />
      ) : (
        <div className="h-[calc(100%-20px)]">
          <TextInput
            placeholder="Search..."
            className="mt-1 px-4 "
            leftIcon={<i className="fa-regular fa-sharp fa-search" />}
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
          <div
            className={clsx("overflow-y-hidden px-4 ", {
              "h-[calc(100%-105px)]":
                selectedCategory === MetricCategory.USER_DEFINED,
              "h-[calc(100%-48px)]":
                selectedCategory !== MetricCategory.USER_DEFINED,
            })}
          >
            <MetricSelectionGroup
              selectedCategory={selectedCategory}
              metrics={filteredMetrics}
              selectedColumnConfigs={selectedColumnConfigs}
              selectManyMetrics={selectManyMetrics}
              selectMetric={selectMetric}
              clearAllByCategory={clearAllByCategory}
              deselectMetric={deselectMetric}
              listClassName="h-[calc(100%-80px)] overflow-y-auto"
            />
          </div>
          {selectedCategory === MetricCategory.USER_DEFINED && (
            <div>
              <div className="border border-b-0 border-t w-full mb-4 mt-1" />
              <div className="px-4">
                <IconButton
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    openModal();
                    closeModal();
                  }}
                >
                  MANAGE CUSTOM METRICS
                </IconButton>
              </div>
            </div>
          )}
        </div>
      )}
    </PanelContainer>
  );
};
